import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { getUserCredential } from "src/lib/cookies";

import { getPayRequestCountByStatus, getPeriodTypes, getCalendarPeriods } from "../services";

/*
 * Data for the Request Status Overview card (graph3).
 *
 * The endpoint has no grouping parameter, so the granularity strip moves the
 * requested window instead: the month, quarter or year containing
 * referenceDate (the page's selected calendar date).
 *
 * Both rows share one 0 -> max axis so their segments line up; the max is the
 * largest row total.
 *
 * This card runs its own period cycle, independent of the Earnings Overview
 * and Hours Worked Trend cards: the Month/Quarter/Year toggle is matched to a
 * period type (select-period-type), which is then used to pull that type's
 * calendar periods (select-calendar) so the period containing referenceDate
 * can be sent as in_period_mstr_key.
 */

const AXIS_STEP = 10;

const squash = (value) => String(value ?? "").toLowerCase().replace(/[^a-z]/g, "");

const GRANULARITY_PERIOD_ALIASES = {
  month: ["month", "monthly"],
  quarter: ["quarter", "quarterly"],
  year: ["year", "yearly"],
};

const findPeriodTypeKey = (periodTypes, granularity) => {
  const aliases = GRANULARITY_PERIOD_ALIASES[granularity] || [granularity];
  const match = periodTypes.find((row) => aliases.includes(squash(row?.period_type_name)));
  return match?.period_type_mstr_key ?? null;
};

// A stable default: scope is an effect dependency, so a fresh {} on every render would refetch forever.
const NO_SCOPE = {};

export const useRequestStatusOverview = (providerId, selectedPeriodName = null, scope = NO_SCOPE) => {
  const dispatch = useDispatch();
  const cookiesData = useMemo(() => getUserCredential(), []);

  const [granularity, setGranularity] = useState("month");
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [periodTypes, setPeriodTypes] = useState([]);
  const [periodMstrKey, setPeriodMstrKey] = useState(null);
  const [calendarPeriods, setCalendarPeriods] = useState([]);

  const requestRef = useRef(0);
  const periodRequestRef = useRef(0);

  /* Period types, fetched once, to translate the Month/Quarter/Year toggle
   * into the period_type_mstr_key select-calendar expects. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const rows = await getPeriodTypes(cookiesData, dispatch);
      if (!cancelled) setPeriodTypes(rows);
    })();

    return () => {
      cancelled = true;
    };
  }, [cookiesData, dispatch]);

  const periodTypeKey = useMemo(
    () => findPeriodTypeKey(periodTypes, granularity),
    [periodTypes, granularity],
  );

  /* Fetch calendar periods for the current period type key */
  useEffect(() => {
    if (!periodTypeKey) {
      setCalendarPeriods([]);
      return undefined;
    }

    const token = periodRequestRef.current + 1;
    periodRequestRef.current = token;

    (async () => {
      const periods = await getCalendarPeriods(cookiesData, dispatch, periodTypeKey);
      if (periodRequestRef.current !== token) return;
      setCalendarPeriods(periods);
    })();

    return () => {
      periodRequestRef.current += 1;
    };
  }, [cookiesData, dispatch, periodTypeKey]);

  /* Find the matching period based on selectedPeriodName */
  useEffect(() => {
    if (!selectedPeriodName || !calendarPeriods.length) {
      setPeriodMstrKey(null);
      return;
    }

    const match = calendarPeriods.find((period) => period.period_name === selectedPeriodName);
    setPeriodMstrKey(match?.period_mstr_key ?? null);
  }, [selectedPeriodName, calendarPeriods]);

  /* Get the selected period's dates */
  const { startDate, endDate } = useMemo(() => {
    if (!periodMstrKey || !calendarPeriods.length) {
      return { startDate: null, endDate: null };
    }

    const period = calendarPeriods.find((p) => p.period_mstr_key === periodMstrKey);
    return {
      startDate: period?.period_start_date ?? null,
      endDate: period?.period_end_date ?? null,
    };
  }, [periodMstrKey, calendarPeriods]);

  useEffect(() => {
    // Only proceed if we have both providerId and a valid period with dates.
    if (!providerId || !periodMstrKey || !startDate || !endDate) {
      return undefined;
    }

    const token = requestRef.current + 1;
    requestRef.current = token;
    setIsLoading(true);

    (async () => {
      const data = await getPayRequestCountByStatus(
        cookiesData,
        dispatch,
        {
          providerId,
          startDate,
          endDate,
          in_period_mstr_key: periodMstrKey,
        },
        scope
      );

      if (requestRef.current !== token) return;

      setGroups(data);
      setIsLoading(false);
    })();

    return () => {
      requestRef.current += 1;
    };
  }, [cookiesData, dispatch, providerId, startDate, endDate, periodMstrKey, granularity, scope]);

  const axisMax = useMemo(() => {
    const largest = groups.reduce((max, group) => Math.max(max, group.total), 0);
    // A zero-wide axis would collapse both bars, so keep one step of room.
    return largest > 0 ? largest : AXIS_STEP;
  }, [groups]);

  /* Ticks read 0, 10, 20, 30 ... and then the exact max, as in the design. */
  const axisTicks = useMemo(() => {
    const ticks = [];
    for (let tick = 0; tick < axisMax; tick += AXIS_STEP) ticks.push(tick);
    // A max that already lands on a step is the last tick; pushing it again
    // would label the same gridline twice.
    if (axisMax % AXIS_STEP !== 0) ticks.push(axisMax);
    return ticks;
  }, [axisMax]);

  const legend = useMemo(
    () =>
      groups.flatMap((group) =>
        group.segments.map((segment) => ({
          key: segment.key,
          label: segment.label,
          color: segment.color,
        })),
      ),
    [groups],
  );

  const isEmpty = groups.every((group) => group.total === 0);

  return {
    granularity,
    setGranularity: useCallback((value) => setGranularity(value), []),
    groups,
    axisMax,
    axisTicks,
    legend,
    isEmpty,
    isLoading,
    calendarPeriods,
  };
};

export default useRequestStatusOverview;
